/**
 * Disputes, deadline first.
 *
 * WHY evidence_due_by is the loudest column: a missed evidence deadline loses the chargeback
 * automatically, so it is the only number on this screen that is time-critical. The engine
 * raises DISPUTE_DEADLINE (R10) on the same threshold this page highlights.
 */
import { useState, type ReactNode } from "react";
import { api } from "../api";
import { Amount } from "../components/Amount";
import { Badge, StatusBadge } from "../components/Badges";
import { EmptyState, ErrorState, Loading } from "../components/States";
import { deadlineLabel, deadlineTone, formatDateTime } from "../format";
import { useAsync } from "../hooks/useAsync";
import { useStore } from "./StoreLayout";

export function Disputes(): ReactNode {
  const store = useStore();
  const [openOnly, setOpenOnly] = useState(true);
  const state = useAsync(() => api.disputes(store.id, { open: openOnly }), [store.id, openOnly]);

  return (
    <>
      <header className="page-head">
        <h1 className="page-title">Disputes</h1>
        <div className="filters" role="group" aria-label="Dispute filter">
          <button type="button" className={`filter${openOnly ? " filter-active" : ""}`} onClick={() => setOpenOnly(true)}>OPEN</button>
          <button type="button" className={`filter${openOnly ? "" : " filter-active"}`} onClick={() => setOpenOnly(false)}>ALL</button>
        </div>
      </header>

      {state.status === "loading" ? <Loading label="Loading disputes" /> : null}
      {state.status === "error" ? <ErrorState error={state.error} onRetry={state.reload} /> : null}
      {state.status === "ready" ? (
        state.data.disputes.length === 0 ? (
          <EmptyState
            title={openOnly ? "No open disputes" : "No disputes recorded"}
            hint="Chargebacks and inquiries show up here as soon as the platform reports them, with the evidence deadline and the money held against them."
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Evidence due</th><th>Dispute</th><th>Status</th><th>Type</th>
                  <th className="num">Amount</th><th className="num">Fee</th><th>Reason</th><th>Order</th><th>Hold</th><th>Initiated</th>
                </tr>
              </thead>
              <tbody>
                {state.data.disputes.map((d) => {
                  const tone = deadlineTone(d.evidence_due_by);
                  return (
                    <tr key={d.id} className={tone === "PAST" ? "row-bad" : tone === "SOON" ? "row-warn" : ""}>
                      <td>
                        {d.evidence_due_by ? (
                          <>
                            <div>{formatDateTime(d.evidence_due_by)}</div>
                            <Badge tone={tone === "PAST" ? "bad" : tone === "SOON" ? "warn" : "neutral"}>{deadlineLabel(d.evidence_due_by)}</Badge>
                          </>
                        ) : (
                          <span className="muted">no deadline</span>
                        )}
                        {d.evidence_sent_at ? <div className="hint">evidence sent {formatDateTime(d.evidence_sent_at)}</div> : null}
                      </td>
                      <td className="mono" title={d.external_id}>{d.external_id.slice(-16)}</td>
                      <td><StatusBadge status={d.status} /></td>
                      <td>{d.type}</td>
                      <td className="num"><Amount minor={d.amount_minor} currency={d.currency} /></td>
                      <td className="num"><Amount minor={d.fee_minor} currency={d.currency} signed /></td>
                      <td>{d.reason_code ?? <span className="muted">—</span>}{d.network_reason_code ? <span className="chip">{d.network_reason_code}</span> : null}</td>
                      <td>{d.order_number ?? <span className="muted">—</span>}</td>
                      <td>{d.hold_status ? <><StatusBadge status={d.hold_status} /> <Amount minor={d.hold_amount_minor} currency={d.currency} /></> : <span className="muted">—</span>}</td>
                      <td>{formatDateTime(d.initiated_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </>
  );
}
